import * as React from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * label / control / helper / error を縦方向に揃える、shadcn/ui の Field 相当コンテナです。
 * 入力ごとの余白をここに集約し、フォーム画面で label と説明文の間隔がばらつかないようにします。
 */
function Field({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field"
      className={cn("flex flex-col gap-3", className)}
      {...props}
    />
  );
}

/**
 * 複数の Field を縦に並べるためのグループです。
 * onboarding のように説明を読みながら進むフォームでは、2 カラムよりも読み順を固定します。
 */
function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn("flex flex-col gap-7", className)}
      {...props}
    />
  );
}

/**
 * radio group など関連する入力群を semantic に束ねる fieldset です。
 */
function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn("flex flex-col gap-4", className)}
      {...props}
    />
  );
}

/**
 * FieldSet の見出しです。単体 Field の label と視覚的に揃えます。
 */
function FieldLegend({ className, ...props }: React.ComponentProps<"legend">) {
  return (
    <legend
      data-slot="field-legend"
      className={cn("text-sm font-medium leading-none", className)}
      {...props}
    />
  );
}

/**
 * 入力の label です。既存 Label のアクセシビリティ挙動を保ったまま Field 系の命名に揃えます。
 */
function FieldLabel({
  className,
  ...props
}: React.ComponentProps<typeof Label>) {
  return <Label data-slot="field-label" className={cn(className)} {...props} />;
}

/**
 * 入力の補足説明です。control の直下に置くことで、読み上げ順と視覚的な対応を揃えます。
 */
function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn("text-xs leading-relaxed text-muted-foreground", className)}
      {...props}
    />
  );
}

/**
 * 入力エラー表示です。将来の validation 追加時に Field 直下へ一貫して置けるようにします。
 */
function FieldError({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-error"
      className={cn("text-sm font-medium text-destructive", className)}
      {...props}
    />
  );
}

export {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
};
